import React, { useState } from "react"
import { CALENDAR_MONTHS } from "../../utils/exports/months"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./stats.scss"

// UI components
import Table from "../../components/structure/table/table"
import Title from "../../components/structure/title/title"
import Tile from "../../components/structure/tile/tile"
import Button from "../../components/ui/button/button"
import Select from "../../components/ui/select/select"

// Build up a list of years for the select field
let yearsObj = {}
let nextYear = Number(moment().startOf("year").add(2, "years").format("YYYY"))
for (let i = nextYear; i >= 2018; i--) {
	yearsObj = {
		[i]: i,
		...yearsObj,
	}
}

// Functional component to return the dashboard for viewing enquiries and their conversions
export default function EnquiryStats() {
	const [loading, setLoading] = useState(false)
	const [summary, setSummary] = useState([])

	const [month, setMonth] = useState("")
	const [year, setYear] = useState("")

	// Process the enquiries and bookings
	const fetchEnquiries = async () => {
		try {
			setLoading(true)
			setSummary([])

			// Get the milliseconds timestamps from the chosen filters
			let fetchStart = 0
			let fetchEnd = 0
			if (month && year) {
				fetchStart = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
				fetchEnd = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()
			} else if (!month && year) {
				fetchStart = moment(`${year}`, "YYYY").startOf("year").valueOf()
				fetchEnd = moment(`${year}`, "YYYY").endOf("year").valueOf()
			}
			const startTimestamp = firebase.firestore.Timestamp.fromMillis(fetchStart)
			const endTimestamp = firebase.firestore.Timestamp.fromMillis(fetchEnd)

			// Pull the enquiries and deleted enquiries from the database
			const [enquiries, deletedEnquiries, bookings] = await Promise.all([
				db.collection("enquiries").where("created", ">=", startTimestamp).where("created", "<=", endTimestamp).orderBy("created", "desc").get(),
				db.collection("enquiries_removed").where("enquiry.created", ">=", startTimestamp).where("enquiry.created", "<=", endTimestamp).orderBy("enquiry.created", "desc").get(),
				db.collection("bookings").where("enquiry.created", ">=", startTimestamp).where("enquiry.created", "<=", endTimestamp).get(),
			])

			// Get the enquiry objects from the documents, only keep the fields we need (exclude archived from stats)
			const enquiryDocs = enquiries.docs
				.filter((doc) => doc.data().archived_from_stats !== true)
				.map((doc) => {
					const data = doc.data()
					return {
						id: doc.id,
						created: data.created,
						client: data.client,
						location: data.location,
						enquired_day: moment(data.created.seconds, "X").format("dddd"),
					}
				})
			const deletedEnquiryDocs = deletedEnquiries.docs
				.filter((doc) => doc.data().archived_from_stats !== true)
				.map((doc) => {
					const data = doc.data().enquiry
					return {
						id: doc.id,
						created: data.created,
						client: data.client,
						location: data.location,
						enquired_day: moment(data.created.seconds, "X").format("dddd"),
					}
				})
			const bookingDocs = bookings.docs.map((doc) => {
				const data = doc.data()
				return {
					created: data.created,
					client: data.client,
					enquiry: {
						location: data.enquiry.location,
						created: data.enquiry.created,
					},
				}
			})

			// Combine both the arrays
			const enquiriesArr = [...enquiryDocs, ...deletedEnquiryDocs]

			// Map over the enquiries, and if we can match the enquiry to a booking, then add the booking to the enquiry
			const enquiriesWithBookings = enquiriesArr.map((enquiry) => {
				// Match the enquiry to a booking on client, location and created seconds
				const booking = bookingDocs.find((doc) => doc.client === enquiry.client && doc.enquiry.location === enquiry.location && doc.enquiry.created.seconds === enquiry.created.seconds)

				// If there is a booking, write "converted" to true, otherwise false
				const converted = booking ? true : false

				// If they converted, work out the number of days between the enquiry and the booking
				const daysBetween = booking ? moment(booking.created.seconds, "X").diff(moment(enquiry.created.seconds, "X"), "days") : null

				// Return the enquiry with the booking details
				return { ...enquiry, booking, converted, daysBetween }
			})

			// Build an array for Mon-Sun, showing the number of enquiries for each day and the % that convert to a booking
			const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
			const daysOfWeekData = daysOfWeek.map((day) => {
				const enquiriesForDay = enquiriesWithBookings.filter((enquiry) => enquiry.enquired_day === day)
				const convertedEnquiries = enquiriesForDay.filter((enquiry) => enquiry.converted)

				// Work out an average of the days taken between enquiries and bookings, only if the enquiry converted
				const avg_conversion_days = convertedEnquiries.length > 0 ? enquiriesForDay.reduce((acc, enquiry) => acc + enquiry.daysBetween, 0) / convertedEnquiries.length : null

				// Work out the percentage of enquiries that converted
				const percentage = (convertedEnquiries.length / enquiriesForDay.length) * 100

				return { day, enquiries: enquiriesForDay.length, converted: convertedEnquiries.length, percentage, avg_conversion_days }
			})

			// Set the summary data into the state
			setSummary(daysOfWeekData)
		} catch (err) {
			console.log("Error fetching details:", err)
		} finally {
			setLoading(false)
		}
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Enquiry Stats</h1>
			</Title>

			<div className="page-filters extra-stats">
				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={yearsObj}
				/>

				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button
						filters={true}
						label="Fetch stats"
						loadingText="Fetching stats..."
						loading={loading}
						onClick={() => fetchEnquiries()}
					/>

					<Button
						label="Clear filters"
						onClick={() => {
							setMonth("")
							setYear("")
							setSummary([])
						}}
					/>
				</div>
			</div>

			<Table
				className="travellers-table"
				headings={["Day", "Total Enquiries", "Conversions", "Conversion Rate", "Avg. Conversion Days"]}
				noResults={summary.length === 0}
				noResultsMessage={"No summary data available"}>
				{summary.map((dayData) => {
					return (
						<tr key={dayData.day}>
							<td>{dayData.day}</td>
							<td>{dayData.enquiries}</td>
							<td>{dayData.converted}</td>
							<td>{dayData.percentage.toFixed(1)}%</td>
							<td>{dayData.avg_conversion_days.toFixed(1)}</td>
						</tr>
					)
				})}

				<tr className="totals-row">
					<td>Totals & Averages:</td>
					<td>{summary.reduce((acc, day) => acc + day.enquiries, 0)}</td>
					<td>{summary.reduce((acc, day) => acc + day.converted, 0)}</td>
					<td>{(summary.reduce((acc, day) => acc + day.percentage, 0) / 7).toFixed(1)}%</td>
					<td>{(summary.reduce((acc, day) => acc + day.avg_conversion_days, 0) / 7).toFixed(1)}</td>
				</tr>
			</Table>
		</Tile>
	)
}
